import { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";

import { HttpAdminApi } from "../../interface/admin-api";
import { userState } from "../../interface/MainInterface";

interface PropsType {
  userState: userState;
}

interface ForbiddenWord {
  pk?: string;
  sk?: string;
  word_id?: string;
  word: string;
  normalized_word?: string;
  use_yn?: "Y" | "N";
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
}

const adminApi = new HttpAdminApi();

const emptyForm: ForbiddenWord = {
  word: "",
  use_yn: "Y",
};

const ForbiddenWordManagement = ({ userState }: PropsType) => {
  const [isLoading, setIsLoading] = useState(false);
  const [forbiddenWordList, setForbiddenWordList] = useState<ForbiddenWord[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState<ForbiddenWord>(emptyForm);

  const filteredList = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return forbiddenWordList;

    return forbiddenWordList.filter((item) => {
      return item.word.toLowerCase().includes(query) || item.normalized_word?.includes(query);
    });
  }, [forbiddenWordList, searchQuery]);

  const formatDate = (value?: string) => {
    if (!value || value.length < 8) return "";
    return `${value.slice(0, 4)}.${value.slice(4, 6)}.${value.slice(6, 8)}`;
  };

  useEffect(() => {
    getForbiddenWordList();
  }, []);

  const getForbiddenWordList = async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.post({ command: "get_forbidden_word_list" });
      if (res.code === "200") {
        setForbiddenWordList(res.response.forbidden_word_list || []);
      } else {
        alert(res.response.error_msg || "금칙어 목록 조회에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setForm(emptyForm);
    setOpenDialog(true);
  };

  const openEditDialog = (item: ForbiddenWord) => {
    setForm({ ...item });
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setForm(emptyForm);
  };

  const saveForbiddenWord = async () => {
    if (!form.word.trim()) {
      alert("금칙어를 입력해주세요.");
      return;
    }

    setIsLoading(true);
    try {
      const res = await adminApi.post({
        command: "save_forbidden_word",
        user_id: userState.id,
        forbidden_word: {
          ...form,
          word: form.word.trim(),
          use_yn: form.use_yn || "Y",
        },
      });

      if (res.code === "200") {
        closeDialog();
        await getForbiddenWordList();
      } else {
        alert(res.response.error_msg || "금칙어 저장에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const deleteForbiddenWord = async (item: ForbiddenWord) => {
    if (!window.confirm(`'${item.word}' 금칙어를 삭제하시겠습니까?`)) return;

    setIsLoading(true);
    try {
      const res = await adminApi.post({
        command: "delete_forbidden_word",
        forbidden_word: item,
      });

      if (res.code === "200") {
        await getForbiddenWordList();
      } else {
        alert(res.response.error_msg || "금칙어 삭제에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "word",
      headerName: "금칙어",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "use_yn",
      headerName: "사용 여부",
      width: 120,
      align: "center",
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.use_yn === "N" ? "미사용" : "사용"}
          color={params.row.use_yn === "N" ? "default" : "primary"}
          size="small"
          variant={params.row.use_yn === "N" ? "outlined" : "filled"}
        />
      ),
    },
    {
      field: "updated_at",
      headerName: "수정일",
      width: 170,
      renderCell: (params: GridRenderCellParams) => formatDate(params.row.updated_at),
    },
    {
      field: "actions",
      headerName: "관리",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="수정">
            <IconButton size="small" color="primary" onClick={() => openEditDialog(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="삭제">
            <IconButton size="small" color="error" onClick={() => deleteForbiddenWord(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", boxSizing: "border-box", p: 4 }}>
      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden" }}>
        <Box sx={{ p: 3, bgcolor: "#f8fafc", borderBottom: "1px solid #e0e0e0" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                금칙어 관리
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                고객 채팅 입력 시 차단할 단어를 관리합니다.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              금칙어 추가
            </Button>
          </Stack>
          <TextField
            size="small"
            fullWidth
            placeholder="금칙어 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mt: 3, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ height: "calc(100vh - 260px)", minHeight: 420 }}>
          <DataGrid
            rows={filteredList}
            columns={columns}
            getRowId={(row) => row.word_id || row.sk || row.word}
            loading={isLoading}
            disableRowSelectionOnClick
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 25,
                },
              },
            }}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      </Card>

      <Dialog open={openDialog} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{form.word_id ? "금칙어 수정" : "금칙어 추가"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="금칙어"
              fullWidth
              value={form.word}
              onChange={(e) => setForm((prev) => ({ ...prev, word: e.target.value }))}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                사용 여부
              </Typography>
              <Switch
                checked={form.use_yn !== "N"}
                onChange={(e) => setForm((prev) => ({ ...prev, use_yn: e.target.checked ? "Y" : "N" }))}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeDialog}>취소</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={saveForbiddenWord}>
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ForbiddenWordManagement;
